import { AfterViewInit, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EventConstants } from '@core/event-constants';
import { ChartService } from '@shared/services/chart.service';
import { EventService } from '@shared/services/event.service';
import * as _ from 'lodash-es';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ChartMappingData } from '@modules/apc/apc/data/chart-mapping-data';
import { HelperService } from '@shared/services/helper.service';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { X_AXIS_TYPE } from '@shared/enums/x-axis-type.enum';

@Component({
  selector: 'app-performance-development-chart',
  templateUrl: './performance-development-chart.component.html',
  styleUrls: ['./performance-development-chart.component.scss'],
})
export class PerformanceDevelopmentChartComponent implements OnInit, AfterViewInit, OnDestroy, AfterViewInit {
  private chartService: ChartService = inject(ChartService);
  private eventService: EventService = inject(EventService);
  private helperService: HelperService = inject(HelperService);
  private customRendererService: CustomRendererService = inject(CustomRendererService);
  private elementRef: ElementRef = inject(ElementRef);
  private observer: MutationObserver | null = null;

  public oldChartSettings: any = null;
  public uniqueChartPostfixId: string;
  public chartIdWithoutHash: string;
  public performanceDevelopmentComment: string = '';
  public xAxisType: string | null;
  public savedDateLines: any;
  public savedXLines: any;

  @Input() public config: any;
  @Input() public testId: number;

  @ViewChild('bokehContainer') bokehContainer!: ElementRef;

  public shouldHideChart: boolean = false;

  public ngOnInit(): void {
    this.generateChartPrefixId();
    this.setXAxisType(this.config);
    this.shouldHideChart = this.hasOnlyOneDateEntry(this.config); // ← add this
  }

  private hasOnlyOneDateEntry(config: any): boolean {
    if (!config?.lines?.length) return true;

    const allDates = config.lines.flatMap((line: any) => line?.source?.x ?? []);
    const uniqueDates = new Set(allDates);

    return uniqueDates.size <= 1;
  }

  public ngAfterViewInit(): void {
    this.setDateAndNumberOfTests(this.config);
    const configCopy: any = this.helperService.makeCopy(this.config);

    this.renderPDChart(configCopy, this.xAxisType);

    setTimeout(() => {
      this.setupDOMObserver();
      this.setupResetButtonListener();
    }, 500);
  }

  setupDOMObserver(): void {
    // Create a mutation observer to watch for changes to the DOM
    this.observer = new MutationObserver((mutations) => {
      // When Bokeh recreates the toolbar, our event listener is lost
      // We need to reattach it whenever the DOM changes
      this.setupResetButtonListener();
    });

    // Start observing the container for changes
    if (this.bokehContainer && this.bokehContainer.nativeElement) {
      this.observer.observe(this.bokehContainer.nativeElement, {
        childList: true, // Watch for changes to child elements
        subtree: true, // Watch the entire subtree
      });
    }
  }

  setupResetButtonListener(): void {
    // Find the reset button in the DOM
    const resetButton = this.elementRef.nativeElement.querySelector('.bk-tool-icon-reset');

    if (resetButton) {
      // First remove any existing event listeners to prevent duplicates
      resetButton.removeEventListener('click', this.handleResetClick);

      // Re-bind the event handler with the current instance
      const boundHandler = this.handleResetClick.bind(this);

      // Store the bound handler on the element itself
      // so we can reference it later for removal
      (resetButton as any)._boundResetHandler = boundHandler;

      // Attach the fresh event listener
      resetButton.addEventListener('click', boundHandler);
    } else {
      console.warn('Reset button not found in the DOM');
    }
  }

  handleResetClick(): void {
    const chartSettings: any = JSON.parse(this.chartService.event.test_settings.chart_settings);

    if (
      chartSettings.hasOwnProperty('performance_development_settings') &&
      chartSettings.performance_development_settings.hasOwnProperty(this.config.name)
    ) {
      chartSettings.performance_development_settings[this.config.name] = {};
      this.chartService.event.test_settings.chart_settings = JSON.stringify(chartSettings);
    }

    this.eventService.BroadcastEvent(EventConstants.APC.SAVE_SETTINGS, { isReset: true });

    // After a reset, Bokeh often recreates the toolbar
    // Schedule a check to reattach the listener if needed
    setTimeout(() => {
      this.setupResetButtonListener();
    }, 100);
  }

  public renderPDChart(config: any, type: string | null): void {
    config.x_axis_type = X_AXIS_TYPE.DATETIME;

    if (type === X_AXIS_TYPE.DATETIME) {
      config.extra_tools.forEach((item: any) => {
        item.tooltips[1][0] = 'Date';
      });
      this.renderPerformanceDevelopmentChart(config, this.testId, this.uniqueChartPostfixId);
    } else if (type === X_AXIS_TYPE.DATE_GAP) {
      // config.lines = this.savedXLines;
      config.extra_tools.forEach((item: any) => {
        // item.tooltips[1][0] = 'Number of tests';
        item.tooltips[1][0] = 'Date';
      });
      const preprocessedPlot: any = this.preprocessPlotData(config);

      this.renderPerformanceDevelopmentChart(preprocessedPlot, this.testId, this.uniqueChartPostfixId);
    }
  }

  public setDateAndNumberOfTests(config: any): void {
    const configCopy: any = this.helperService.makeCopy(config);
    const xLineCopy: any = this.helperService.makeCopy(config.lines);
    xLineCopy.forEach((item: any) => {
      const date: any = item.source.date.map((item: any, index: number) => index + 1);
      const x: any = item.source.x.map((item: any, index: number) => index + 1);

      item.source.date = [date[0], date[date.length - 1]];
      item.source.x = [x[0], x[x.length - 1]];
    });

    this.savedDateLines = configCopy.lines;
    this.savedXLines = xLineCopy;
  }

  public onToggleChanged(event: any): void {
    this.customRendererService.empty(this.uniqueChartPostfixId);
    const configCopy: any = this.helperService.makeCopy(this.config);
    configCopy.x_axis_type = X_AXIS_TYPE.DATETIME;

    if (event === X_AXIS_TYPE.DATETIME) {
      configCopy.lines = this.savedDateLines;
      configCopy.extra_tools.forEach((item: any) => {
        item.tooltips[1][0] = 'Date';
      });
      this.renderPerformanceDevelopmentChart(configCopy, this.testId, this.uniqueChartPostfixId);
    } else if (event === X_AXIS_TYPE.DATE_GAP) {
      // configCopy.lines = this.savedXLines;
      configCopy.extra_tools.forEach((item: any) => {
        // item.tooltips[1][0] = 'Number of tests';
        item.tooltips[1][0] = 'Date';
      });

      const preprocessedPlot: any = this.preprocessPlotData(configCopy);

      this.renderPerformanceDevelopmentChart(preprocessedPlot, this.testId, this.uniqueChartPostfixId);
    }

    localStorage.setItem(this.config.name + '_PD_toggle_state' + this.testId, event);
    localStorage.setItem('chart_changed', 'true');
  }

  private preprocessPlotData(plot: any): any {
    // Create a deep copy of the plot to avoid modifying the original
    const processedPlot = this.helperService.makeCopy(plot);

    // Only process if we have lines and we're dealing with datetime x-axis
    if (
      processedPlot &&
      processedPlot.lines &&
      processedPlot.lines.length > 0 &&
      (processedPlot.x_axis_type === 'datetime' || processedPlot.x_axis_type === 'date_gap')
    ) {
      // Extract all unique dates and their data points
      const uniqueDates = new Map();

      try {
        // Collect all unique dates from all lines
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.x && Array.isArray(line.source.x)) {
            line.source.x.forEach((dateVal: any, index: number) => {
              try {
                const dateObj = new Date(dateVal);

                if (isNaN(dateObj.getTime())) {
                  console.warn(`Invalid date found: ${dateVal}`);
                  return;
                }

                const timestamp = dateObj.getTime();

                // Format date for display
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                const formattedDate = `${day}.${month}.${year}`;

                uniqueDates.set(timestamp, {
                  formatted: formattedDate,
                  originalDate: dateVal,
                });
              } catch (error) {
                console.error(`Error processing date: ${dateVal}`, error);
              }
            });
          }
        });

        if (uniqueDates.size === 0) {
          return processedPlot;
        }

        // Convert map to sorted array
        const sortedTimestamps = Array.from(uniqueDates.keys()).sort((a, b) => a - b);

        // Now let's distribute the points - we'll use simple integer indices
        const positionMap = new Map();
        sortedTimestamps.forEach((timestamp, index) => {
          positionMap.set(timestamp, index);
        });

        // Update source data for each line
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.x && Array.isArray(line.source.x)) {
            line.source.original_x = [...line.source.x];

            // Keep the date in a format that works for tooltips
            if (line.source.date) {
              line.source.date_display = line.source.date;
            }

            // Convert dates to indices
            line.source.x = line.source.x.map((dateVal: any) => {
              try {
                const timestamp = new Date(dateVal).getTime();
                return positionMap.has(timestamp) ? positionMap.get(timestamp) : 0;
              } catch (e) {
                return 0;
              }
            });
          }
        });

        // Same for circles
        if (processedPlot.circles) {
          processedPlot.circles.forEach((circle: any) => {
            if (circle.source && circle.source.x && Array.isArray(circle.source.x)) {
              circle.source.original_x = [...circle.source.x];

              if (circle.source.date) {
                circle.source.date_display = circle.source.date;
              }

              circle.source.x = circle.source.x.map((dateVal: any) => {
                try {
                  const timestamp = new Date(dateVal).getTime();
                  return positionMap.has(timestamp) ? positionMap.get(timestamp) : 0;
                } catch (e) {
                  return 0;
                }
              });
            }
          });
        }

        // Store the x-axis configuration
        processedPlot.custom_x_ticks = Array.from(positionMap.values());
        processedPlot.date_labels = sortedTimestamps.map((ts) => uniqueDates.get(ts).formatted);

        // IMPORTANT: Modify the x-axis type to ensure it works with the data
        processedPlot.original_x_axis_type = processedPlot.x_axis_type;
        processedPlot.x_axis_type = 'linear'; // Use linear instead of null/datetime

        // Set explicit range values - use integers to avoid floating point issues
        const numPoints = processedPlot.custom_x_ticks.length;
        processedPlot.x_range = {
          bounds: ['auto'],
          plot_range: [-0.5, numPoints - 0.5], // Integer range with padding
        };

        // Flag for custom axis
        processedPlot.use_custom_x_axis = true;

        // FIX Y-AXIS RANGE BASED ON ACTUAL DATA VALUES
        // Collect all y values to determine proper range
        const allYValues: any = [];

        // Extract all y values from the lines
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.y && Array.isArray(line.source.y)) {
            // Filter out any non-numeric or invalid values
            const validYValues = line.source.y.filter(
              (val: any) => val !== undefined && val !== null && !isNaN(parseFloat(val))
            );

            // Convert strings to numbers if needed
            const numericYValues = validYValues.map((val: any) => (typeof val === 'string' ? parseFloat(val) : val));

            allYValues.push(...numericYValues);
          }
        });

        // Only process if we have valid y values
        if (allYValues.length > 0) {
          // Find min and max y values
          const minY = Math.min(...allYValues);
          const maxY = Math.max(...allYValues);

          // Add padding (20% on top and bottom)
          const yRange = Math.max(0.001, maxY - minY); // Ensure non-zero range
          const padding = yRange * 0.2;
          const yMin = minY - padding;
          const yMax = maxY + padding;

          // Update main y-axis range
          if (processedPlot.y_range) {
            processedPlot.y_range.plot_range = [yMin, yMax];
          } else {
            processedPlot.y_range = {
              bounds: ['auto'],
              plot_range: [yMin, yMax],
            };
          }

          // Also update any extra y ranges if they exist
          if (processedPlot.extra_y_ranges) {
            Object.keys(processedPlot.extra_y_ranges).forEach((key) => {
              const range = processedPlot.extra_y_ranges[key];
              if (range && range.plot_range) {
                range.plot_range = [yMin, yMax];
              }
            });
          }
        }
      } catch (error) {
        console.error('Error processing plot data:', error);
        return processedPlot;
      }
    }

    return processedPlot;
  }

  public setXAxisType(config: any): void {
    this.xAxisType = localStorage.getItem(config.name + '_PD_toggle_state' + this.testId) || config.x_axis_type;
  }

  public generateChartPrefixId(): void {
    this.uniqueChartPostfixId =
      ChartConstants.PERFORMANCE_DEVELOPMENT.ELEMENT_ID + '_' + this.helperService.generateRandomString();
    this.chartIdWithoutHash = this.uniqueChartPostfixId.substring(1);
  }

  private renderComments(): void {
    const pdSettings: string = this.chartService.event.test_settings.performance_development;
    if (pdSettings) {
      this.performanceDevelopmentComment = pdSettings;
    } else if (
      this.chartService.event.hasOwnProperty('default_text') &&
      this.chartService.event.default_text &&
      this.chartService.event.default_text.hasOwnProperty('performance_development')
    ) {
      this.performanceDevelopmentComment = this.chartService.event.default_text.performance_development;
    }
    this.oldChartSettings = this.helperService.makeCopy(this.chartService.event.test_settings);
  }

  private renderPerformanceDevelopmentChart(plot: any, testId: number, elementId: string): void {
    try {
      const payload = {
        plot,
        elementId,
        testId,
        type: ChartConstants.PERFORMANCE_DEVELOPMENT.ID,
        legend: 'legends',
        mappingData: ChartMappingData,
      };
      this.chartService.generateSeparatePerformanceDevelopment(payload.plot, payload.elementId, payload.testId, false);
    } catch (error) {
      console.log('renderPerformanceDevelopmentChart failed with ID: ' + testId, error);
    }
  }

  public processComment(): void {
    this.chartService.event.is_chart_changed = !_.isEqual(this.oldChartSettings, this.chartService.event.test_settings);
  }

  public prepareChartSettings(): void {
    // if (this.chartService?.event?.id) {
    //   const performanceDevelopmentXSettings: string | null = localStorage.getItem('PDsettings_' + this.chartService?.event?.id,);
    //   if (performanceDevelopmentXSettings != null) {
    //     this.chartService.event.test_settings.chart_settings.PDsettings = JSON.parse(performanceDevelopmentXSettings,);
    //   }
    //
    //   const performanceDevelopmentYSettings = localStorage.getItem('PDYsettings_' + this.chartService?.event?.id,);
    //   if (performanceDevelopmentYSettings != null) {
    //     this.chartService.event.test_settings.chart_settings.PDYsettings = JSON.parse(performanceDevelopmentYSettings);
    //   }
    // }
  }

  toggleChartVisibility() {
    this.shouldHideChart = !this.shouldHideChart;
  }

  public ngOnDestroy(): void {
    // Clean up the observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Clean up event listeners to prevent memory leaks
    const resetButton = this.elementRef.nativeElement.querySelector('.bk-tool-icon-reset');
    if (resetButton && (resetButton as any)._boundResetHandler) {
      resetButton.removeEventListener('click', (resetButton as any)._boundResetHandler);
    }

    localStorage.removeItem(this.config.name + '_PD_toggle_state' + this.testId);
    localStorage.removeItem(this.config.name + '_PDXLinearSettings_' + this.testId);
    localStorage.removeItem(this.config.name + '_PDYLinearSettings_' + this.testId);
  }
}
